<template>
  <v-container fluid>
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <h1 class="text-h4 font-weight-bold">داشبورد</h1>
        <p class="text-subtitle-1 grey--text">خلاصه آمار و اطلاعات فروشگاه</p>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading">
      <v-col cols="12" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-subtitle-1">در حال بارگذاری...</p>
      </v-col>
    </v-row>

    <!-- Error State -->
    <v-alert v-if="error" type="error" dismissible @input="error = false" class="mb-4">
      خطا در بارگذاری اطلاعات. لطفاً صفحه را بروزرسانی کنید.
    </v-alert>

    <template v-if="!loading && statistics">
      <!-- Statistics Cards -->
      <v-row class="mb-6">
        <!-- Total Orders -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" color="blue darken-1" dark>
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <div>
                  <p class="text-subtitle-2 mb-1">مجموع سفارشات</p>
                  <h2 class="text-h3 font-weight-bold">{{ statistics.orders }}</h2>
                </div>
                <v-icon size="48" class="stat-icon">mdi-cart-outline</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Total Products -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" color="green darken-1" dark>
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <div>
                  <p class="text-subtitle-2 mb-1">مجموع محصولات</p>
                  <h2 class="text-h3 font-weight-bold">{{ statistics.products }}</h2>
                </div>
                <v-icon size="48" class="stat-icon">mdi-package-variant</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Total Users -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" color="purple darken-1" dark>
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <div>
                  <p class="text-subtitle-2 mb-1">مجموع کاربران</p>
                  <h2 class="text-h3 font-weight-bold">{{ statistics.users }}</h2>
                </div>
                <v-icon size="48" class="stat-icon">mdi-account-group</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Total Categories -->
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" color="orange darken-1" dark>
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <div>
                  <p class="text-subtitle-2 mb-1">دسته‌بندی‌ها</p>
                  <h2 class="text-h3 font-weight-bold">{{ statistics.categories }}</h2>
                </div>
                <v-icon size="48" class="stat-icon">mdi-folder-multiple</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Revenue Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="4">
          <v-card class="revenue-card">
            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-icon color="success" class="ml-2">mdi-cash-multiple</v-icon>
                <span class="text-subtitle-2">درآمد امروز</span>
              </div>
              <h3 class="text-h4 font-weight-bold success--text">{{ formatPrice(revenue.today) }}</h3>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-card class="revenue-card">
            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-icon color="info" class="ml-2">mdi-calendar-month</v-icon>
                <span class="text-subtitle-2">درآمد این ماه</span>
              </div>
              <h3 class="text-h4 font-weight-bold info--text">{{ formatPrice(revenue.this_month) }}</h3>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-card class="revenue-card">
            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="ml-2">mdi-currency-usd</v-icon>
                <span class="text-subtitle-2">کل درآمد</span>
              </div>
              <h3 class="text-h4 font-weight-bold primary--text">{{ formatPrice(revenue.total) }}</h3>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <!-- Quick Actions -->
        <v-col cols="12" md="4">
          <v-card class="quick-actions-card">
            <v-card-title class="pb-2">
              <v-icon class="ml-2" color="primary">mdi-lightning-bolt</v-icon>
              دسترسی سریع
            </v-card-title>
            <v-card-text>
              <v-btn color="primary" block class="mb-3" to="/products">
                <v-icon left>mdi-plus</v-icon>
                افزودن محصول جدید
              </v-btn>
              <v-btn color="green" dark block class="mb-3" to="/orders">
                <v-icon left>mdi-cart-outline</v-icon>
                مدیریت سفارشات
              </v-btn>
              <v-btn color="orange" dark block class="mb-3" to="/categories">
                <v-icon left>mdi-folder-multiple</v-icon>
                مدیریت دسته‌بندی‌ها
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Order Status Summary -->
          <v-card class="mt-4">
            <v-card-title class="pb-2">
              <v-icon class="ml-2" color="blue">mdi-chart-pie</v-icon>
              وضعیت سفارشات
            </v-card-title>
            <v-card-text>
              <div class="order-status-item">
                <div class="d-flex justify-space-between align-center mb-3">
                  <div class="d-flex align-center">
                    <v-icon color="warning" class="ml-2">mdi-clock-outline</v-icon>
                    <span>در انتظار</span>
                  </div>
                  <v-chip color="warning" small>{{ statistics.pending_orders }}</v-chip>
                </div>
              </div>

              <div class="order-status-item">
                <div class="d-flex justify-space-between align-center mb-3">
                  <div class="d-flex align-center">
                    <v-icon color="info" class="ml-2">mdi-progress-clock</v-icon>
                    <span>در حال پردازش</span>
                  </div>
                  <v-chip color="info" small>{{ statistics.processing_orders }}</v-chip>
                </div>
              </div>

              <div class="order-status-item">
                <div class="d-flex justify-space-between align-center mb-3">
                  <div class="d-flex align-center">
                    <v-icon color="success" class="ml-2">mdi-check-circle</v-icon>
                    <span>تکمیل شده</span>
                  </div>
                  <v-chip color="success" small>{{ statistics.completed_orders }}</v-chip>
                </div>
              </div>

              <div class="order-status-item">
                <div class="d-flex justify-space-between align-center">
                  <div class="d-flex align-center">
                    <v-icon color="error" class="ml-2">mdi-alert-circle</v-icon>
                    <span>موجودی صفر</span>
                  </div>
                  <v-chip color="error" small>{{ statistics.out_of_stock }}</v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Recent Orders -->
        <v-col cols="12" md="8">
          <v-card>
            <v-card-title class="pb-2">
              <v-icon class="ml-2" color="blue">mdi-format-list-bulleted</v-icon>
              آخرین سفارشات
              <v-spacer></v-spacer>
              <v-btn small text color="primary" to="/orders">مشاهده همه</v-btn>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-data-table :headers="orderHeaders" :items="recentOrders" :items-per-page="5" hide-default-footer class="orders-table">
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip :color="getStatusColor(item.status)" small dark>
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.total_price`]="{ item }">
                  <span class="font-weight-bold">{{ formatPrice(item.total_price) }}</span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Low Stock Products -->
          <v-card v-if="lowStockProducts.length > 0" class="mt-4">
            <v-card-title class="pb-2">
              <v-icon class="ml-2" color="warning">mdi-alert</v-icon>
              محصولات با موجودی کم
            </v-card-title>
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th scope="col" class="text-right">محصول</th>
                      <th scope="col" class="text-right">رنگ</th>
                      <th scope="col" class="text-right">موجودی</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in lowStockProducts" :key="item.product_id">
                      <td>{{ item.product_title }}</td>
                      <td>{{ item.color }}</td>
                      <td>
                        <v-chip small :color="item.stock < 3 ? 'error' : 'warning'" dark>
                          {{ item.stock }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
export default {
  name: 'DashboardView',

  data() {
    return {
      loading: true,
      error: false,
      statistics: null,
      revenue: null,
      recentOrders: [],
      lowStockProducts: [],
      orderHeaders: [
        { text: 'شناسه', value: 'uuid', align: 'right' },
        { text: 'نام مشتری', value: 'user_name', align: 'right' },
        { text: 'مبلغ', value: 'total_price', align: 'right' },
        { text: 'وضعیت', value: 'status', align: 'right' },
        { text: 'تاریخ', value: 'created_at', align: 'right' },
      ],
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    fetchData() {
      this.loading = true
      this.error = false

      this.$store
        .dispatch('fetchDashboardStatistics')
        .then((response) => {
          const data = response.data
          this.statistics = data.statistics
          this.revenue = data.revenue
          this.recentOrders = data.recent_orders
          this.lowStockProducts = data.low_stock_products
        })
        .catch((error) => {
          console.error('Error fetching dashboard data:', error)
          this.error = true
        })
        .finally(() => {
          this.loading = false
        })
    },

    formatPrice(price) {
      if (!price) return '۰ تومان'
      return new Intl.NumberFormat('fa-IR').format(price) + ' تومان'
    },

    getStatusColor(status) {
      const colors = {
        pending: 'warning',
        processing: 'info',
        completed: 'success',
        cancelled: 'error',
        delivered: 'green',
      }
      return colors[status] || 'grey'
    },

    getStatusText(status) {
      const texts = {
        pending: 'در انتظار',
        processing: 'در حال پردازش',
        completed: 'تکمیل شده',
        cancelled: 'لغو شده',
        delivered: 'تحویل داده شده',
      }
      return texts[status] || status
    },
  },
}
</script>

<style scoped>
.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.stat-icon {
  opacity: 0.8;
}

.revenue-card {
  transition: all 0.3s ease;
  border-left: 4px solid #1976d2;
}

.revenue-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.quick-actions-card {
  border-top: 4px solid #1976d2;
}

.orders-table {
  font-size: 14px;
}

.order-status-item {
  border-bottom: 1px solid #e0e0e0;
}

.order-status-item:last-child {
  border-bottom: none;
}
</style>
